import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useAuthStore, usePlayerStore } from '../stores'
import { useMessageStore } from '../stores/useMessageStore'
import {
  MessageSquare, Search, Send, Archive, Trash2, Check, Loader2
} from 'lucide-react'

function PlayerMessages({ embedded }: { embedded?: boolean }) {
  const { user } = useAuthStore()
  const { players, fetchPlayers } = usePlayerStore()
  const {
    conversations,
    messages,
    profiles,
    loading,
    error,
    activeConversationId,
    fetchConversations,
    fetchProfilesByIds,
    fetchMessages,
    sendMessage,
    markAsRead,
    archiveConversation,
    deleteConversation,
    setActiveConversation,
  } = useMessageStore()

  const [searchQuery, setSearchQuery] = useState('')
  const [messageInput, setMessageInput] = useState('')
  const [sending, setSending] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)
  const [archiveError, setArchiveError] = useState<string | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const currentPlayer = useMemo(() => {
    if (!user) return null
    return players.find(p =>
      p.scout?.toLowerCase() === user.email?.toLowerCase() ||
      p.email?.toLowerCase() === user.email?.toLowerCase() ||
      p.name?.toLowerCase() === user.name?.toLowerCase()
    ) || null
  }, [user, players])

  useEffect(() => {
    if (players.length === 0) {
      fetchPlayers()
    }
  }, [players.length, fetchPlayers])

  useEffect(() => {
    if (currentPlayer?.id) {
      fetchConversations(currentPlayer.id)
    }
  }, [currentPlayer?.id, fetchConversations])

  useEffect(() => {
    if (conversations.length > 0) {
      const ids = conversations.map(c => c.other_user_id || c.scout_id).filter(Boolean)
      fetchProfilesByIds(ids)
    }
  }, [conversations, fetchProfilesByIds])

  useEffect(() => {
    if (activeConversationId) {
      fetchMessages(activeConversationId).then(() => {
        markAsRead(activeConversationId)
      })
    }
  }, [activeConversationId, fetchMessages, markAsRead])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages[activeConversationId || '']])

  const getOtherUser = useCallback((conv: { player_id: string; other_user_id?: string; scout_id: string }) => {
    if (!currentPlayer) return { name: 'Unknown', role: 'scout' }
    const otherId = conv.player_id === currentPlayer.id ? (conv.other_user_id || conv.scout_id) : conv.player_id
    return profiles[otherId] || { name: 'Loading...', role: 'scout', id: otherId }
  }, [currentPlayer, profiles])

  const filteredConversations = useMemo(() => {
    if (!searchQuery) return conversations
    return conversations.filter(c => {
      const other = getOtherUser(c)
      return other.name.toLowerCase().includes(searchQuery.toLowerCase())
    })
  }, [conversations, searchQuery, getOtherUser])

  const activeConversation = conversations.find(c => c.id === activeConversationId)
  const activeMessages = messages[activeConversationId || ''] || []

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!activeConversationId || !messageInput.trim() || sending) return

    setSending(true)
    try {
      await sendMessage(activeConversationId, messageInput.trim(), user?.id || '')
      setMessageInput('')
      if (textareaRef.current) textareaRef.current.style.height = 'auto'
    } catch (err) {
      console.error('Failed to send message:', err)
    } finally {
      setSending(false)
    }
  }

  const handleArchive = async (convId: string) => {
    try {
      setArchiveError(null)
      await archiveConversation(convId)
    } catch (err) {
      setArchiveError('Failed to archive conversation')
    }
  }

  const handleDelete = async (convId: string) => {
    if (deleteConfirm === convId) {
      try {
        setDeleteError(null)
        await deleteConversation(convId)
        setDeleteConfirm(null)
      } catch (err) {
        setDeleteError('Failed to delete conversation')
      }
    } else {
      setDeleteConfirm(convId)
      setDeleteError(null)
    }
  }

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessageInput(e.target.value)
    e.target.style.height = 'auto'
    e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`
  }

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr)
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)
    if (minutes < 1) return 'Just now'
    if (minutes < 60) return `${minutes}m ago`
    if (hours < 24) return `${hours}h ago`
    if (days < 7) return `${days}d ago`
    return date.toLocaleDateString()
  }

  if (!user || !currentPlayer) {
    return (
      <div className="h-full flex items-center justify-center bg-[#050910]">
        <div className="text-center">
          <MessageSquare className="mx-auto text-gray-500 mb-4" size={48} />
          <h3 className="text-lg font-semibold text-white">No Profile Found</h3>
          <p className="text-gray-400 mt-2">Your player profile hasn't been created yet.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full bg-[#050910] flex flex-col">
      {!embedded && (
        <header className="border-b border-gray-800 bg-gray-950/80 backdrop-blur-sm sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center gap-3">
                <MessageSquare size={24} className="text-green-500" />
                <div>
                  <h2 className="text-lg font-bold text-white">Messages</h2>
                  <p className="text-xs text-gray-500">Communicate with scouts and clubs</p>
                </div>
              </div>
            </div>
          </div>
        </header>
      )}

      <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
        {/* Conversations Sidebar - visible on desktop */}
        <div className="hidden md:block">
          <aside className="w-full md:w-80 lg:w-96 border-r border-gray-800 bg-gray-950 flex flex-col flex-shrink-0">
            <div className="p-4 border-b border-gray-800">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search conversations..."
                  className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/5 rounded-xl text-white placeholder-gray-600 focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none transition text-sm"
                />
              </div>
            </div>

            {loading ? (
              <div className="flex-1 flex items-center justify-center">
                <Loader2 className="text-green-500 animate-spin" size={24} />
              </div>
            ) : error ? (
              <div className="flex-1 flex items-center justify-center text-red-400 text-sm p-4">{error}</div>
            ) : filteredConversations.length === 0 ? (
              <div className="flex-1 flex items-center justify-center p-4">
                <div className="text-center">
                  <MessageSquare className="mx-auto text-gray-500 mb-4" size={48} />
                  <h3 className="text-lg font-semibold text-white">No Messages Yet</h3>
                  <p className="text-gray-400 mt-2 text-sm">Scouts and clubs will message you here. You can reply once they reach out.</p>
                </div>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto">
                <div className="space-y-1 p-3">
                  {filteredConversations.map((conv) => {
                    const other = getOtherUser(conv)
                    return (
                      <button
                      key={conv.id}
                      onClick={() => setActiveConversation(conv.id)}
                      className={`w-full text-left p-3 rounded-xl transition touch-manipulation ${
                        activeConversationId === conv.id
                          ? 'bg-green-500/10 border border-green-500/20'
                          : 'hover:bg-white/5'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-black text-green-400">{other.name?.charAt(0) || '?'}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <h4 className="text-sm font-bold text-white truncate">{other.name}</h4>
                            <span className="text-[10px] text-gray-500 uppercase tracking-widest flex-shrink-0 ml-2">
                              {conv.last_message_at ? formatTime(conv.last_message_at) : 'New'}
                            </span>
                          </div>
                          {conv.last_message && (
                            <p className="text-xs text-gray-500 truncate mt-1">{conv.last_message}</p>
                          )}
                        </div>
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>
          )}
        </aside>
        </div>

        {/* Mobile conversations list - simple stack */}
        <div className="block md:hidden">
          <div className="flex-1 flex flex-col">
            <div className="p-4 border-b border-gray-800">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search conversations..."
                  className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/5 rounded-xl text-white placeholder-gray-600 focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none transition text-sm"
                />
              </div>
            </div>

            {loading ? (
              <div className="flex-1 flex items-center justify-center">
                <Loader2 className="text-green-500 animate-spin" size={24} />
              </div>
            ) : error ? (
              <div className="flex-1 flex items-center justify-center text-red-400 text-sm p-4">{error}</div>
            ) : filteredConversations.length === 0 ? (
              <div className="flex-1 flex items-center justify-center p-4">
                <div className="text-center">
                  <MessageSquare className="mx-auto text-gray-500 mb-4" size={48} />
                  <h3 className="text-lg font-semibold text-white">No Messages Yet</h3>
                  <p className="text-gray-400 mt-2 text-sm">Scouts and clubs will message you here. You can reply once they reach out.</p>
                </div>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto">
                <div className="space-y-1 p-3">
                  {filteredConversations.map((conv) => {
                    const other = getOtherUser(conv)
                    return (
                      <button
                        key={conv.id}
                        onClick={() => setActiveConversation(conv.id)}
                        className={`w-full text-left p-3 rounded-xl transition touch-manipulation ${
                          activeConversationId === conv.id
                            ? 'bg-green-500/10 border border-green-500/20'
                            : 'hover:bg-white/5'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-black text-green-400">{other.name?.charAt(0) || '?'}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <h4 className="text-sm font-bold text-white truncate">{other.name}</h4>
                              <span className="text-[10px] text-gray-500 uppercase tracking-widest flex-shrink-0 ml-2">
                                {conv.last_message_at ? formatTime(conv.last_message_at) : 'New'}
                              </span>
                            </div>
                            {conv.last_message && (
                              <p className="text-xs text-gray-500 truncate mt-1">{conv.last_message}</p>
                            )}
                          </div>
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div className="flex-1 flex flex-col min-w-0">
          {activeConversation ? (
            <>
              <header className="border-b border-gray-800 bg-gray-950 p-4 flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-black text-green-400">{getOtherUser(activeConversation)?.name?.charAt(0) || '?'}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-bold text-white truncate">{getOtherUser(activeConversation)?.name}</h3>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleArchive(activeConversation.id)}
                      className="p-2 text-gray-500 hover:text-yellow-400 hover:bg-yellow-500/10 rounded-xl transition touch-manipulation"
                      title="Archive"
                    >
                      <Archive size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(activeConversation.id)}
                      className="p-2 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-xl transition touch-manipulation"
                      title={deleteConfirm === activeConversation.id ? 'Tap again to confirm delete' : 'Delete conversation'}
                    >
                      {deleteConfirm === activeConversation.id ? <Check size={18} className="text-red-400" /> : <Trash2 size={18} />}
                    </button>
                  </div>
                </div>
              </header>

              {(archiveError || deleteError) && (
                <div className="bg-red-900/20 border border-red-700/50 text-red-400 text-xs px-4 py-2 mx-4 mt-2 rounded-xl flex items-center justify-between">
                  <span>{archiveError || deleteError}</span>
                  <button onClick={() => { setArchiveError(null); setDeleteError(null) }} className="ml-4 text-red-300 hover:text-red-100">Dismiss</button>
                </div>
              )}

              <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-4">
                {activeMessages.length === 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <MessageSquare className="mx-auto text-gray-600 mb-4" size={48} />
                      <p className="text-gray-500">No messages yet. Start the conversation!</p>
                    </div>
                  </div>
                ) : (
                  <>
                    {activeMessages.map((msg) => {
                      const isOwn = msg.sender_id === user?.id
                      const senderProfile = profiles[msg.sender_id]
                      return (
                        <div key={msg.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[85%] sm:max-w-[70%] ${isOwn ? 'order-2' : 'order-1'}`}>
                            {!isOwn && (
                              <p className="text-[10px] text-gray-500 mb-1 px-1">{senderProfile?.name || 'Unknown'}</p>
                            )}
                            <div className={`flex flex-col ${isOwn ? 'items-end' : 'items-start'}`}>
                              <div className={`relative px-4 py-2.5 rounded-2xl ${isOwn ? 'bg-green-500 text-black' : 'bg-white/5 border border-white/10 text-white'}`}>
                                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                              </div>
                              <div className="flex items-center gap-2 mt-1 px-1">
                                <span className="text-[10px] text-gray-600">{formatTime(msg.created_at)}</span>
                                {isOwn && <Check size={12} className={msg.is_read ? 'text-green-500' : 'text-gray-500'} />}
                              </div>
                            </div>
                          </div>
                          {isOwn && <div className="w-6 flex-shrink-0" />}
                        </div>
                      )
                    })}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>

              <div className="border-t border-gray-800 bg-gray-950 p-3 sm:p-4 flex-shrink-0">
                <form onSubmit={handleSendMessage} className="flex items-end gap-2">
                  <div className="flex-1">
                    <textarea
                      ref={textareaRef}
                      value={messageInput}
                      onChange={handleTextareaChange}
                      placeholder="Type a message..."
                      rows={1}
                      className="w-full px-4 py-2.5 bg-white/5 border border-white/5 rounded-2xl text-white placeholder-gray-600 focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none transition text-sm font-bold resize-none max-h-32 min-h-[44px]"
                      disabled={sending}
                      onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(e) } }}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={!messageInput.trim() || sending}
                    className="p-3 bg-green-500 border border-green-500 rounded-xl text-black hover:bg-green-400 transition disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 touch-manipulation min-h-[44px]"
                    aria-label="Send message"
                  >
                    {sending ? <Loader2 size={20} className="animate-spin" /> : <Send size={20} />}
                  </button>
                </form>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center px-4">
                <MessageSquare className="mx-auto text-gray-600 mb-4" size={64} />
                <h3 className="text-xl font-semibold text-white">Select a Conversation</h3>
                <p className="text-gray-400 mt-2">Choose a chat from the sidebar</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default PlayerMessages